import type {
  Artwork,
  AuthResponse,
  VisitorStats,
  Product,
  ProductSize,
  Order,
  ShippingDetails,
  Category,
  AboutContent,
  BlogPost,
} from '../types'

export type VisitPayload = {
  path: string
  referrer?: string
  utm?: Record<string, string>
  device?: {
    userAgent?: string
    platform?: string
    screen?: { width?: number; height?: number }
    language?: string
  }
  sessionId?: string
  locale?: string
}

export type AnalyticsEventPayload = {
  type: string
  sessionId?: string
  artworkId?: number
  value?: number
  durationMs?: number
  metadata?: Record<string, unknown>
}

export interface TrendingAnalyticsItem {
  artworkId: number
  engagementMs: number
}

export interface TranslationEntry {
  id: string
  key: string
  source: string
  translations: Record<string, string>
  createdAt?: string
  updatedAt?: string
}

const RAW_API_BASE_URL =
  import.meta.env?.VITE_API_BASE_URL ||
  (typeof window !== 'undefined'
    ? `${window.location.origin.replace(/\/+$/, '')}/api`
    : '/api')

const API_BASE_URL = RAW_API_BASE_URL.replace(/\/+$/, '')
const ANALYTICS_ENDPOINT = `${API_BASE_URL}/analytics/events`
const ANALYTICS_EVENT_QUEUE: AnalyticsEventPayload[] = []
let analyticsIdleHandle: number | null = null
let analyticsDelayTimer: number | null = null
const ANALYTICS_FETCH_TIMEOUT_MS = 1200
let hasWindowLoaded = typeof document === 'undefined' ? true : document.readyState === 'complete'
const waitForWindowLoad: Promise<void> =
  typeof window === 'undefined'
    ? Promise.resolve()
    : hasWindowLoaded
      ? Promise.resolve()
      : new Promise((resolve) => {
          window.addEventListener(
            'load',
            () => {
              hasWindowLoaded = true
              resolve()
            },
            { once: true },
          )
        })

type IdleWin = typeof window & {
  requestIdleCallback?: (callback: IdleRequestCallback, options?: IdleRequestOptions) => number
  cancelIdleCallback?: (handle: number) => void
}

const flushAnalyticsQueue = (useBeacon = true) => {
  if (!ANALYTICS_EVENT_QUEUE.length) return
  const events = ANALYTICS_EVENT_QUEUE.splice(0, ANALYTICS_EVENT_QUEUE.length)
  events.forEach((entry) => {
    sendAnalyticsEvent(entry, useBeacon).catch(() => undefined)
  })
}

const scheduleAnalyticsFlush = () => {
  if (typeof window === 'undefined') return
  if (!hasWindowLoaded) {
    waitForWindowLoad.then(() => scheduleAnalyticsFlush())
    return
  }
  if (analyticsIdleHandle !== null || analyticsDelayTimer !== null) return

  const startIdle = () => {
    analyticsDelayTimer = null
    const run = () => {
      analyticsIdleHandle = null
      flushAnalyticsQueue(true)
    }
    const idleWindow = window as IdleWin
    if (typeof idleWindow.requestIdleCallback === 'function') {
      analyticsIdleHandle = idleWindow.requestIdleCallback(run, { timeout: 2000 })
    } else {
      analyticsIdleHandle = window.setTimeout(run, 1500)
    }
  }

  analyticsDelayTimer = window.setTimeout(startIdle, 1600)
}

const sendAnalyticsEvent = async (payload: AnalyticsEventPayload, preferBeacon = true) => {
  const body = JSON.stringify(payload)
  const canUseBeacon = typeof navigator !== 'undefined' && typeof navigator.sendBeacon === 'function'
  if (canUseBeacon) {
    const blob = new Blob([body], { type: 'application/json' })
    const sent = navigator.sendBeacon(ANALYTICS_ENDPOINT, blob)
    if (sent) return
  }

    const dispatchLowPriorityFetch = () => {
      const controller = typeof AbortController !== 'undefined' ? new AbortController() : null
      const timeout = controller ? setTimeout(() => controller.abort(), ANALYTICS_FETCH_TIMEOUT_MS) : null
      const requestInit: RequestInit & { priority?: 'high' | 'low' | 'auto' } = {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body,
        keepalive: true,
        signal: controller?.signal,
        priority: 'low',
        mode: 'cors',
        credentials: 'omit',
      }
      return fetch(ANALYTICS_ENDPOINT, requestInit)
        .catch(() => undefined)
      .finally(() => {
        if (timeout) {
          clearTimeout(timeout)
        }
      })
  }

  if (preferBeacon && typeof window !== 'undefined') {
    window.setTimeout(() => {
      void dispatchLowPriorityFetch()
    }, 1500)
    return
  }

  await dispatchLowPriorityFetch()
}

if (typeof window !== 'undefined') {
  window.addEventListener(
    'visibilitychange',
    () => {
      if (document.visibilityState === 'hidden') {
        flushAnalyticsQueue(true)
      }
    },
    { passive: true },
  )
  window.addEventListener(
    'pagehide',
    () => {
      flushAnalyticsQueue(true)
    },
    { passive: true },
  )
}

async function handleResponse<T>(response: Response): Promise<T> {
  if (!response.ok) {
    if (response.status === 401 && typeof window !== 'undefined') {
      console.warn('API responded with 401, clearing admin session')
      localStorage.removeItem('artist_admin_token')
      if (window.location.pathname.startsWith('/admin')) {
        window.location.href = '/admin/login'
      }
    }
    const errorBody = await response.json().catch(() => ({}))
    const message = errorBody.message || response.statusText
    throw new Error(message)
  }
  return response.json()
}

export async function fetchArtworks(): Promise<Artwork[]> {
  const response = await fetch(`${API_BASE_URL}/artworks`)
  return handleResponse<Artwork[]>(response)
}

export async function fetchArtwork(id: number): Promise<Artwork> {
  const response = await fetch(`${API_BASE_URL}/artworks/${id}`)
  return handleResponse<Artwork>(response)
}

export async function incrementArtworkView(id: number): Promise<void> {
  await fetch(`${API_BASE_URL}/artworks/${id}/view`, { method: 'POST' })
}

export async function adminLogin(password: string, username = 'artist'): Promise<AuthResponse> {
  const response = await fetch(`${API_BASE_URL}/auth/login`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ username, password }),
  })
  return handleResponse<AuthResponse>(response)
}

export async function createArtwork(formData: FormData, token: string): Promise<Artwork> {
  const response = await fetch(`${API_BASE_URL}/artworks`, {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${token}`,
    },
    body: formData,
  })
  return handleResponse<Artwork>(response)
}

export async function updateArtwork(
  id: number,
  formData: FormData,
  token: string,
): Promise<Artwork> {
  const response = await fetch(`${API_BASE_URL}/artworks/${id}`, {
    method: 'PUT',
    headers: {
      Authorization: `Bearer ${token}`,
    },
    body: formData,
  })
  return handleResponse<Artwork>(response)
}

export async function deleteArtwork(id: number, token: string): Promise<void> {
  const response = await fetch(`${API_BASE_URL}/artworks/${id}`, {
    method: 'DELETE',
    headers: {
      Authorization: `Bearer ${token}`,
    },
  })
  if (!response.ok) {
    const body = await response.json().catch(() => ({}))
    throw new Error(body.message || 'Failed to delete artwork')
  }
}

export async function fetchOrders(token: string): Promise<Order[]> {
  const response = await fetch(`${API_BASE_URL}/orders`, {
    headers: { Authorization: `Bearer ${token}` },
  })
  return handleResponse<Order[]>(response)
}

export async function fetchOrderStatus(statusKey: string | number): Promise<Order> {
  const response = await fetch(`${API_BASE_URL}/orders/status/${statusKey}`)
  return handleResponse<Order>(response)
}

export async function updateOrderStatus(
  id: number,
  status: string,
  token: string,
): Promise<Order> {
  const response = await fetch(`${API_BASE_URL}/orders/${id}`, {
    method: 'PATCH',
    headers: {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ status }),
  })
  return handleResponse<Order>(response)
}

export async function deleteOrder(id: number, token: string): Promise<void> {
  const response = await fetch(`${API_BASE_URL}/orders/${id}`, {
    method: 'DELETE',
    headers: {
      Authorization: `Bearer ${token}`,
    },
  })
  if (!response.ok) {
    const body = await response.json().catch(() => ({}))
    throw new Error(body.message || 'Failed to delete order')
  }
}

export async function fetchCategories(): Promise<Category[]> {
  const response = await fetch(`${API_BASE_URL}/categories`)
  return handleResponse<Category[]>(response)
}

export async function createCategory(name: string, token: string): Promise<Category> {
  const response = await fetch(`${API_BASE_URL}/categories`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
    body: JSON.stringify({ name }),
  })
  return handleResponse<Category>(response)
}

export async function deleteCategory(id: string, token: string): Promise<{ id: string; deleted: boolean }> {
  const response = await fetch(`${API_BASE_URL}/categories/${id}`, {
    method: 'DELETE',
    headers: { Authorization: `Bearer ${token}` },
  })
  return handleResponse<{ id: string; deleted: boolean }>(response)
}

export async function fetchTranslations(): Promise<TranslationEntry[]> {
  const response = await fetch(`${API_BASE_URL}/translations`)
  return handleResponse<TranslationEntry[]>(response)
}

export async function createTranslationEntry(
  payload: { key: string; source: string; translations?: Record<string, string> },
  token: string,
): Promise<TranslationEntry> {
  const response = await fetch(`${API_BASE_URL}/translations`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
    body: JSON.stringify(payload),
  })
  return handleResponse<TranslationEntry>(response)
}

export async function updateTranslationEntry(
  id: string,
  payload: { key?: string; source?: string; translations?: Record<string, string> },
  token: string,
): Promise<TranslationEntry> {
  const response = await fetch(`${API_BASE_URL}/translations/${id}`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
    body: JSON.stringify(payload),
  })
  return handleResponse<TranslationEntry>(response)
}

export async function deleteTranslationEntry(id: string, token: string): Promise<void> {
  const response = await fetch(`${API_BASE_URL}/translations/${id}`, {
    method: 'DELETE',
    headers: { Authorization: `Bearer ${token}` },
  })
  if (!response.ok) {
    const errorBody = await response.json().catch(() => ({}))
    throw new Error(errorBody.message || 'Failed to delete translation')
  }
}

export async function fetchAbout(): Promise<AboutContent> {
  const response = await fetch(`${API_BASE_URL}/about`)
  return handleResponse<AboutContent>(response)
}

export async function updateAbout(
  payload: { text: string; imageUrl: string; translations?: Record<string, string> | null },
  token: string,
): Promise<AboutContent> {
  const response = await fetch(`${API_BASE_URL}/about`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
    body: JSON.stringify(payload),
  })
  return handleResponse<AboutContent>(response)
}

export async function fetchBlogPosts(): Promise<BlogPost[]> {
  const response = await fetch(`${API_BASE_URL}/blog`)
  return handleResponse<BlogPost[]>(response)
}

export async function fetchBlogPost(id: string): Promise<BlogPost> {
  const response = await fetch(`${API_BASE_URL}/blog/${id}`)
  return handleResponse<BlogPost>(response)
}

type BlogPayload = { title: string; content: string; imageUrl?: string; artworkIds?: string[] }

export async function createBlogPost(payload: BlogPayload, token: string) {
  const response = await fetch(`${API_BASE_URL}/blog`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
    body: JSON.stringify(payload),
  })
  return handleResponse<BlogPost>(response)
}

export async function updateBlogPost(id: string, payload: Partial<BlogPayload>, token: string) {
  const response = await fetch(`${API_BASE_URL}/blog/${id}`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
    body: JSON.stringify(payload),
  })
  return handleResponse<BlogPost>(response)
}

export async function deleteBlogPost(id: string, token: string) {
  const response = await fetch(`${API_BASE_URL}/blog/${id}`, {
    method: 'DELETE',
    headers: { Authorization: `Bearer ${token}` },
  })
  return handleResponse<{ id: string; deleted: boolean }>(response)
}

type CustomProductPayload = {
  title: string
  price: number
  currency?: string
}

export async function createPayPalOrder(payload: {
  artworkId?: number
  customProduct?: CustomProductPayload
}) {
  const response = await fetch(`${API_BASE_URL}/paypal/order`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  })
  return handleResponse<{ id: string }>(response)
}

type CapturePayload = {
  artworkId?: number
  customTitle?: string
  referenceUrls?: string[]
  sessionId?: string
  buyerNote?: string
  buyerEmail?: string
  shippingDetails?: ShippingDetails
}

export async function capturePayPalOrder(orderId: string, payload: CapturePayload) {
  const response = await fetch(`${API_BASE_URL}/paypal/capture`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ orderId, ...payload }),
  })
  return handleResponse<{ status: string; detail: unknown; order?: Order }>(response)
}

export async function createApplePaySession(payload: { validationUrl: string; domain?: string }) {
  const response = await fetch(`${API_BASE_URL}/apple-pay/session`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  })
  return handleResponse<Record<string, unknown>>(response)
}

export async function submitApplePayPayment(payload: {
  token: unknown
  amount: number
  currency: string
  items: Array<{ name?: string; title?: string; qty?: number; quantity?: number; price: number }>
  customerEmail?: string
  buyerEmail?: string
  referenceSessionId?: string
  referenceUrls?: string[]
  buyerNote?: string
  shippingDetails?: ShippingDetails | null
  customTitle?: string
  orderTitle?: string
  appleShippingContact?: unknown
  appleBillingContact?: unknown
}) {
  const response = await fetch(`${API_BASE_URL}/apple-pay/pay`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  })
  return handleResponse<{ status: string; order?: Order; monobank?: unknown }>(response)
}

export async function submitGooglePayPayment(payload: {
  token: string
  amount: number
  currency: string
  items: Array<{ name?: string; title?: string; qty?: number; quantity?: number; price: number }>
  customerEmail?: string
  buyerEmail?: string
  referenceSessionId?: string
  referenceUrls?: string[]
  buyerNote?: string
  shippingDetails?: ShippingDetails | null
  customTitle?: string
  orderTitle?: string
}) {
  const response = await fetch(`${API_BASE_URL}/google-pay/pay`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  })
  return handleResponse<{ status: string; order?: Order; detail?: unknown }>(response)
}

export async function createMonobankInvoice(payload: {
  amount: number
  currency: string
  items: Array<{ name: string; qty: number; price: number }>
  customerEmail?: string
  redirectUrl?: string
  referenceSessionId?: string
  referenceUrls?: string[]
  buyerNote?: string
  shippingDetails?: ShippingDetails | null
  customTitle?: string
}) {
  const response = await fetch(`${API_BASE_URL}/monobank/invoice`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  })
  return handleResponse<{ invoiceId?: string; pageUrl?: string; reference?: string }>(response)
}

export async function fetchMonobankInvoice(invoiceId: string) {
  const response = await fetch(`${API_BASE_URL}/monobank/invoice/${encodeURIComponent(invoiceId)}`)
  return handleResponse<{
    invoiceId: string
    status: string
    orderId?: number
    order?: Order | null
  }>(response)
}

export async function recordVisit(payload: VisitPayload): Promise<{ country?: string; sessionId?: string }> {
  const response = await fetch(`${API_BASE_URL}/analytics/visit`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  })
  if (response.status === 204) {
    return {}
  }
  if (!response.ok) {
    const errorBody = await response.json().catch(() => ({}))
    const message = errorBody.message || response.statusText
    throw new Error(message)
  }
  return response.json().catch(() => ({}))
}

export async function logAnalyticsEvent(payload: AnalyticsEventPayload) {
  if (typeof window === 'undefined') {
    await sendAnalyticsEvent(payload, false)
    return
  }
  ANALYTICS_EVENT_QUEUE.push(payload)
  scheduleAnalyticsFlush()
}

export async function fetchTrendingAnalytics(country?: string): Promise<TrendingAnalyticsItem[]> {
  const query = country ? `?country=${encodeURIComponent(country)}` : ''
  const response = await fetch(`${API_BASE_URL}/analytics/trending${query}`)
  const data = await handleResponse<{ items: TrendingAnalyticsItem[] }>(response)
  return Array.isArray(data.items) ? data.items : []
}

export async function fetchVisitorStats(token: string): Promise<VisitorStats> {
  const response = await fetch(`${API_BASE_URL}/analytics/visitors`, {
    headers: {
      Authorization: `Bearer ${token}`,
    },
  })
  return handleResponse<VisitorStats>(response)
}

export async function fetchProducts(): Promise<Product[]> {
  const response = await fetch(`${API_BASE_URL}/products`)
  return handleResponse<Product[]>(response)
}

export async function createProductSize(
  productId: number,
  payload: {
    labelImperial: string
    labelMetric?: string
    price: number
    variantPricing?: ProductSize['variantPricing']
    variantLabels?: ProductSize['variantLabels']
  },
  token: string,
): Promise<ProductSize> {
  const response = await fetch(`${API_BASE_URL}/products/${productId}/sizes`, {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(payload),
  })
  return handleResponse<ProductSize>(response)
}

export async function updateProductSize(
  productId: number,
  sizeId: number,
  payload: {
    labelImperial?: string
    labelMetric?: string
    label?: string
    price?: number
    variantPricing?: ProductSize['variantPricing'] | null
    variantLabels?: ProductSize['variantLabels'] | null
  },
  token: string,
): Promise<ProductSize> {
  const response = await fetch(`${API_BASE_URL}/products/${productId}/sizes/${sizeId}`, {
    method: 'PATCH',
    headers: {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(payload),
  })
  return handleResponse<ProductSize>(response)
}

export async function deleteProductSize(productId: number, sizeId: number, token: string) {
  const response = await fetch(`${API_BASE_URL}/products/${productId}/sizes/${sizeId}`, {
    method: 'DELETE',
    headers: { Authorization: `Bearer ${token}` },
  })
  if (!response.ok) {
    const body = await response.json().catch(() => ({}))
    throw new Error(body.message || 'Failed to delete size')
  }
}

export async function updateProduct(
  productId: number,
  payload: Partial<Omit<Product, 'id' | 'sizes' | 'slug'>>,
  token: string,
): Promise<Product> {
  const response = await fetch(`${API_BASE_URL}/products/${productId}`, {
    method: 'PATCH',
    headers: {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(payload),
  })
  return handleResponse<Product>(response)
}

export async function recordReferenceUpload(payload: {
  sessionId: string
  productSlug: string
  url: string
}) {
  await fetch(`${API_BASE_URL}/references`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  })
}

export async function removeReferenceUpload(payload: { sessionId: string; url: string }) {
  await fetch(`${API_BASE_URL}/references`, {
    method: 'DELETE',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  })
}

export async function sendContactMessage(payload: { name: string; email: string; message: string }) {
  const response = await fetch(`${API_BASE_URL}/contact`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  })
  const data = await response.json().catch(() => ({}))
  if (!response.ok) {
    const message =
      (data && typeof data.message === 'string' && data.message.trim()) ||
      'Unable to send your message. Please try again.'
    throw new Error(message)
  }
  return data
}
